import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";

const INQUIRY_TYPES = [
  { key: "rollpress", label: "Roll Press 제어 문의", icon: "ri-stack-line" },
  { key: "servovalve", label: "Servo Valve 문의", icon: "ri-equalizer-2-line" },
  { key: "hpu", label: "Hydraulic Unit 문의", icon: "ri-database-2-line" },
  { key: "motion", label: "Motion Controller 문의", icon: "ri-cpu-line" },
  { key: "drive", label: "Servo Drive / Motor 문의", icon: "ri-flashlight-line" },
  { key: "sensor", label: "Sensor 문의", icon: "ri-radar-line" },
  { key: "repair", label: "수리 및 납품 문의", icon: "ri-tools-line" },
  { key: "tech", label: "기술지원 문의", icon: "ri-customer-service-2-line" },
];

const MAX_UPLOAD_SIZE = 8 * 1024 * 1024;

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const presetType = searchParams.get("type");
  const [selectedType, setSelectedType] = useState<string>(() => {
    if (presetType === "quote") return "rollpress";
    if (presetType === "tech") return "tech";
    if (presetType === "product") return "servovalve";
    return "rollpress";
  });
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (presetType === "quote") setSelectedType("rollpress");
    else if (presetType === "tech") setSelectedType("tech");
    else if (presetType === "product") setSelectedType("servovalve");
  }, [presetType]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg("");

    if (message.length > 500) {
      setErrorMsg("문의 내용은 500자 이내로 작성해주세요.");
      return;
    }

    const form = e.currentTarget;
    const formData = new FormData(form);

    const attachment = formData.get("attachment");
    if (attachment instanceof File && attachment.size > MAX_UPLOAD_SIZE) {
      setStatus("error");
      setErrorMsg("첨부파일은 8MB 이하만 전송할 수 있습니다.");
      return;
    }

    const hpValue = formData.get("website_alt");
    if (hpValue && String(hpValue).trim() !== "") {
      setStatus("success");
      form.reset();
      setMessage("");
      return;
    }
    formData.delete("website_alt");

    const selectedLabel = INQUIRY_TYPES.find((t) => t.key === selectedType)?.label || "";
    formData.set("inquiry_type", selectedLabel);

    try {
      setStatus("submitting");
      const res = await fetch("/", {
        method: "POST",
        body: formData,
      });
      if (!res.ok) throw new Error("network");
      setStatus("success");
      form.reset();
      setMessage("");
    } catch {
      setStatus("error");
      setErrorMsg("전송 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
    }
  };

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Contact"
        title="제품 · 기술 · 솔루션 문의"
        subtitle="제품 사양, 도면, 회로도, 기존 장비 정보를 함께 전달해주시면 보다 정확한 검토가 가능합니다."
        crumbs={[{ label: "HOME", to: "/" }, { label: "Contact" }]}
        imageUrl="https://readdy.ai/api/search-image?query=Industrial%20engineer%20answering%20phone%20call%20at%20clean%20modern%20office%20desk%20with%20technical%20drawings%20and%20laptop%2C%20cool%20professional%20lighting%2C%20editorial%20B2B%20engineering%20photography%2C%20muted%20steel%20gray%20and%20cool%20blue%20palette&width=1920&height=900&seq=mctech-contact-hero-01&orientation=landscape"
      />

      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Inquiry types */}
          <aside className="lg:col-span-4 space-y-5">
            <div className="bg-background-100 border border-background-200 rounded-xl p-6">
              <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-accent-700 mb-3">
                Inquiry Type
              </div>
              <h2 className="text-xl font-heading font-extrabold text-foreground-950 mb-4">
                문의 유형 선택
              </h2>
              <div className="space-y-2">
                {INQUIRY_TYPES.map((t) => {
                  const active = selectedType === t.key;
                  return (
                    <button
                      type="button"
                      key={t.key}
                      onClick={() => setSelectedType(t.key)}
                      className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-md border transition-colors cursor-pointer whitespace-nowrap ${
                        active
                          ? "bg-primary-600 border-primary-600 text-background-50"
                          : "bg-background-50 border-background-200 text-foreground-800 hover:border-primary-300"
                      }`}
                    >
                      <i className={`${t.icon} w-4 h-4 flex items-center justify-center`} />
                      <span className="text-sm font-semibold">{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="bg-primary-800 text-background-50 rounded-xl p-6 relative overflow-hidden">
              <div className="absolute inset-0 bp-grid opacity-15" />
              <div className="relative">
                <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-accent-200 mb-2">
                  Direct Contact
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <i className="ri-mail-line w-5 h-5 flex items-center justify-center text-accent-300 mt-0.5" />
                    <div>
                      <div className="font-bold">motion@imctech.co.kr</div>
                      <div className="text-background-200 text-xs">24시간 접수</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-map-pin-2-line w-5 h-5 flex items-center justify-center text-accent-300 mt-0.5" />
                    <div>
                      <div className="font-bold">경기도 용인시 기흥구 흥덕중앙로 120 1209호</div>
                      <div className="text-background-200 text-xs">본사 / 기술 지원 센터</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-8">
            <form
              name="quote-inquiry"
              data-netlify="true"
              encType="multipart/form-data"
              netlify-honeypot="website_alt"
              onSubmit={handleSubmit}
              className="bg-background-100 border border-background-200 rounded-xl p-6 md:p-8"
            >
              {/* Netlify form-name identifier */}
              <input type="hidden" name="form-name" value="quote-inquiry" />

              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-accent-700">
                    Inquiry Form
                  </div>
                  <h2 className="text-2xl font-heading font-extrabold text-foreground-950 mt-1">
                    문의 양식
                  </h2>
                </div>
                <span className="hidden md:inline text-xs font-semibold text-secondary-600">
                  * 표시는 필수 항목입니다.
                </span>
              </div>

              {/* hidden inquiry type */}
              <input type="hidden" name="inquiry_type" value={INQUIRY_TYPES.find((t) => t.key === selectedType)?.label || ""} />

              {/* honeypot — visually hidden, bots fill it, real users don't */}
              <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="nl-hp" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="회사명 *" name="company" placeholder="예: MC TECH" required />
                <Field label="담당자명 *" name="name" placeholder="홍길동" required />
                <Field label="연락처 *" name="phone" placeholder="010-0000-0000" required />
                <Field label="이메일 *" name="email" type="email" placeholder="name@company.com" required />
                <Field label="문의 제품" name="product" placeholder="예: MOOG D661 Servo Valve" />
                <Field label="장비명" name="equipment" placeholder="예: Battery Roll Press Line" />
              </div>

              <div className="mt-4">
                <label className="block text-xs font-semibold text-foreground-800 mb-1.5">
                  문의 내용 *
                </label>
                <textarea
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="장비 사양, 제어 요구사항, 문의하실 내용을 자유롭게 작성해주세요. (최대 500자)"
                  className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none text-sm text-foreground-900 placeholder:text-secondary-500 resize-none"
                />
                <div className="mt-1 flex justify-between text-[11px] text-secondary-600">
                  <span>도면, 회로도, 사양서가 있다면 함께 첨부해주세요.</span>
                  <span>{message.length} / 500</span>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-xs font-semibold text-foreground-800 mb-1.5">
                  첨부파일 (도면 · 회로도 · 사양서 등)
                </label>
                <div className="flex items-center gap-3 bg-background-50 border border-dashed border-background-300 rounded-md px-4 py-4 hover:border-primary-400">
                  <i className="ri-attachment-2 w-5 h-5 flex items-center justify-center text-primary-600" />
                  <input
                    type="file"
                    name="attachment"
                    className="text-sm text-foreground-800 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-primary-100 file:text-primary-800 file:font-semibold file:text-xs cursor-pointer"
                  />
                </div>
                <p className="mt-1 text-[11px] text-secondary-600">
                  첨부파일은 1개, 전체 전송 용량 8MB 이하만 가능합니다.
                </p>
              </div>

              {/* Submit */}
              <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <p className="text-xs text-secondary-700">
                  문의해주신 내용은 영업일 기준 1–2일 내 답변드립니다.
                </p>
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-background-50 font-semibold text-sm whitespace-nowrap cursor-pointer"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center" />
                      전송 중...
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line w-4 h-4 flex items-center justify-center" />
                      문의 전송하기
                    </>
                  )}
                </button>
              </div>

              {status === "success" && (
                <div className="mt-5 flex items-start gap-3 bg-accent-100 border border-accent-200 rounded-md p-4">
                  <i className="ri-checkbox-circle-fill w-5 h-5 flex items-center justify-center text-accent-700 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-bold text-accent-900">견적 문의가 정상적으로 접수되었습니다.</div>
                    <div className="text-accent-800">담당자가 빠른 시일 내 답변드리겠습니다.</div>
                  </div>
                </div>
              )}
              {status === "error" && errorMsg && (
                <div className="mt-5 flex items-start gap-3 bg-background-50 border border-background-300 rounded-md p-4">
                  <i className="ri-error-warning-fill w-5 h-5 flex items-center justify-center text-primary-700 mt-0.5" />
                  <div className="text-sm text-foreground-900">{errorMsg}</div>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function Field({
  label,
  name,
  placeholder,
  type = "text",
  required,
}: {
  label: string;
  name: string;
  placeholder?: string;
  type?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-foreground-800 mb-1.5">
        {label}
      </label>
      <input
        type={type}
        name={name}
        required={required}
        placeholder={placeholder}
        className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none text-sm text-foreground-900 placeholder:text-secondary-500"
      />
    </div>
  );
}
